'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ImageField, RichTextField } from '@prismicio/client';
import { isFilled } from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { HorizontalContentCard } from '@/components/redesign/HorizontalContentCards';
import {
  StepBody,
  StepTitle,
} from '@/components/redesign/NumberedIconStepCard';

type Props = {
  step: {
    card_icon: ImageField;
    card_title: RichTextField;
    card_description: RichTextField;
  };
  index: number;
};

// `useLayoutEffect` warns during SSR because it can't measure the DOM there.
// Fall back to `useEffect` on the server (where it is a no-op anyway) so the
// warning doesn't fire; on the client we still use `useLayoutEffect` so the
// tile is capped before the first paint and there's no visible flash.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const MOBILE_ICON_SIZE_PX = 86;

/**
 * Card layout that pairs a text column (numbered step + title + body) with a
 * right-side icon tile.
 *
 * Two layouts, one per breakpoint family — chosen so the 300px mobile slot
 * (that the `ScrollRail` uses to leave a peek of the next card) stays
 * comfortable to read:
 *
 * Mobile (< sm):
 *   Column layout. Row 1 is the header — step number on the left, a small
 *   fixed-size icon square on the right (mirrors `NumberedIconStepCard`'s
 *   established header-row pattern in the sibling slice). Row 2 spans the
 *   full card width with title + description. Text gets the entire card
 *   width for wrapping, which is the biggest readability win at 300px.
 *
 * sm and up:
 *   The side-by-side layout that's already working. Text column on the left
 *   with `flex-1`, tile on the right at a fixed `w-*` per breakpoint,
 *   `self-stretch` for height, and a JS-driven `max-width` cap that keeps
 *   the tile's rendered width ≤ 4/3 of its height.
 *
 * Contract preserved across both layouts:
 *   1. Only the text drives the card's height. Neither the mobile 56px
 *      icon square (fixed size, contributes nothing to text col height)
 *      nor the sm+ tile (`self-stretch`, no `min-height`, no `aspect-*`)
 *      can make the card taller than the text needs.
 *   2. Text width has priority. The sm+ tile has a fixed reservation the
 *      text lays out around; the JS cap can only make the tile *narrower*,
 *      never wider.
 *   3. sm+ tile width/height ratio never exceeds 4:3.
 *   4. Tile is transparent — image assets with their own background render
 *      as-is.
 *
 * Kept as a sibling of `NumberedIconStepCard` so slices using that card's
 * fixed-size green tile stay untouched.
 */
export function RightIconStepCard({ step, index }: Props) {
  const hasIcon = isFilled.image(step.card_icon);
  const textRef = useRef<HTMLDivElement>(null);
  const tileRef = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(() => {
    if (!hasIcon) {
      return;
    }
    const text = textRef.current;
    const tile = tileRef.current;
    if (!text || !tile) {
      return;
    }

    const compute = () => {
      // When the sm+ tile is hidden (mobile viewport, CSS `hidden`), skip:
      // there's nothing to cap and the tile isn't participating in layout.
      if (tile.offsetParent === null) {
        tile.style.maxWidth = '';
        return;
      }

      // Measure the text column's *minimum* height by temporarily removing
      // the sm+ tile from layout — the text then renders at maximum
      // available width and produces the shortest possible height. Any
      // width we later give the tile makes the text narrower, so its real
      // height is always ≥ this measurement.
      const previousDisplay = tile.style.display;
      tile.style.display = 'none';
      const minHeight = text.getBoundingClientRect().height;
      tile.style.display = previousDisplay;

      // Cap tile width at 4/3 of that minimum height. Because the tile
      // stretches to the actual (≥ minHeight) row height, the rendered
      // width/height ratio ends up ≤ 4/3 — never wider than 4:3.
      const cap = Math.round(minHeight * (4 / 3));
      tile.style.maxWidth = `${cap}px`;
    };

    compute();
    window.addEventListener('resize', compute);
    if (typeof document !== 'undefined' && document.fonts?.ready) {
      document.fonts.ready.then(compute).catch(() => {});
    }
    return () => window.removeEventListener('resize', compute);
  }, [hasIcon]);

  const stepLabel = String(index + 1).padStart(2, '0');

  return (
    <HorizontalContentCard className="flex flex-col gap-4 p-6 sm:flex-row sm:items-stretch sm:gap-6">
      <div ref={textRef} className="flex min-w-0 flex-1 flex-col gap-4">
        {/*
          Header row: step number always visible; the small mobile icon
          badge is only shown below `sm`. `justify-between` positions the
          two ends of the row at mobile, and gracefully collapses to a
          leading step number at sm+ when the icon is display: none.
        */}
        <div className="flex items-center justify-between">
          <span className="border-l border-[#4b0fea] pl-3 text-[18px] font-medium leading-[1.5] text-[#444]">
            {stepLabel}
          </span>
          {hasIcon && (
            <div
              className="relative shrink-0 rounded-[8px] sm:hidden"
              style={{
                width: MOBILE_ICON_SIZE_PX,
                height: MOBILE_ICON_SIZE_PX,
              }}
            >
              <PrismicNextImage
                field={step.card_icon}
                fill
                sizes={`${MOBILE_ICON_SIZE_PX}px`}
                className="object-contain"
                fallbackAlt=""
              />
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <StepTitle
            field={step.card_title}
            className="text-[22px] font-semibold leading-[1.5] text-[#041118]"
          />
          <StepBody
            field={step.card_description}
            className="text-[16px] font-light leading-[1.5] text-[#041118] md:text-[18px]"
          />
        </div>
      </div>

      {hasIcon && (
        // sm+ tile — hidden below `sm` (mobile uses the badge inside the
        // header row instead). Fixed width per breakpoint, transparent
        // background, and the JS `max-width` cap enforces width ≤ 4/3 ×
        // height on the actual row height.
        <div
          ref={tileRef}
          className="relative hidden shrink-0 self-stretch rounded-[8px] sm:block sm:w-[160px] md:w-[180px] lg:w-[200px]"
        >
          <PrismicNextImage
            field={step.card_icon}
            fill
            sizes="200px"
            className="object-contain"
            fallbackAlt=""
          />
        </div>
      )}
    </HorizontalContentCard>
  );
}
